import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { PAGINATION_LIMIT } from "../../utils/consts";
import { compareObjects } from "../../utils/compare";

export const apaApi = createApi({
  reducerPath: "apaApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      headers.set("Content-Type", "application/json");
      const token = getState().auth?.token?.accessToken;
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getSignalsList: builder.query({
      query: ({
        limit = PAGINATION_LIMIT,
        offset = 0,
        name = [],
        client = [],
        strategy = [],
        ticker = [],
        direction = "",
        exchange = "",
        orderBy = "name",
      }) => {
        const directionFilter = direction ? `&direction=${direction}` : "";
        const exchangeFilter = exchange ? `&exchange=${exchange}` : "";
        const stageFilter = name.length
          ? "&" + name.map((i) => `stage_id=${i}`).join("&")
          : "";
        const clientFilter = client.length
          ? "&" + client.map((i) => `client_id=${i}`).join("&")
          : "";
        const tickerFilter = ticker.length
          ? "&" + ticker.map((i) => `ticker=${i}`).join("&")
          : "";
        const strategyFilter = strategy.length
          ? "&" + strategy.map((i) => `strategy=${i}`).join("&")
          : "";
        const order = orderBy ? `&order_by=${orderBy}` : "";

        return `/apa/signals?limit=${limit}&offset=${offset}${order}${stageFilter}${clientFilter}${tickerFilter}${strategyFilter}${directionFilter}${exchangeFilter}`;
      },
      method: "GET",
      // refetch only on real arg change
      forceRefetch: ({ currentArg, previousArg }) =>
        !compareObjects(currentArg, previousArg),
      // cache key depends on filters, not on offset (for pagination)
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const {
          name = [],
          client = [],
          strategy = [],
          ticker = [],
          direction = "",
          exchange = "",
          orderBy = "name",
          limit = PAGINATION_LIMIT,
        } = queryArgs || {};
        const arr = (a) => [...a].sort().join(",");
        return `${endpointName}|nm=${arr(name)}|cl=${arr(client)}|stg=${arr(
          strategy,
        )}|tk=${arr(ticker)}|dir=${direction}|ex=${exchange}|ob=${orderBy}|lim=${limit}`;
      },
      // mutate cache
      merge: (currentCache, incoming, { arg }) => {
        const isFirstPage = (arg?.offset ?? 0) === 0;
        if (!currentCache?.rows) {
          currentCache.rows = [];
          currentCache.summary = null;
          currentCache.isNextPage = true;
        }
        if (isFirstPage) {
          currentCache.rows = incoming.rows;
        } else {
          currentCache.rows.push(...incoming.rows);
        }
        currentCache.summary = incoming.summary;
        currentCache.isNextPage = incoming.isNextPage;
      },
      // derive isNextPage by limit
      transformResponse: (response, _meta, arg) => {
        const rows = Array.isArray(response?.rows) ? response.rows : [];
        const limit = arg?.limit ?? PAGINATION_LIMIT;
        return {
          rows,
          summary: response?.summary,
          isNextPage: rows.length === limit,
        };
      },
      providesTags: ["SignalList"],
    }),

    syncSignalsList: builder.mutation({
      query: () => ({
        url: "/apa/signals/sync",
        method: "POST",
      }),
    }),

    // avoid network call on empty/short strategy
    searchSignalByStrategy: builder.query({
      async queryFn(arg, _api, _extra, fetchWithBQ) {
        const strategy = (arg?.strategy || "").trim();
        if (strategy.length < 2) return { data: [] }; // no request
        const res = await fetchWithBQ(
          `/apa/signals/strategies?limit=100&strategy=${encodeURIComponent(
            strategy,
          )}`,
        );
        if (res.error) return { error: res.error };
        const data = Array.isArray(res.data?.rows) ? res.data.rows : res.data;
        return { data: Array.isArray(data) ? data : [] };
      },
      method: "GET",
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.strategy !== previousArg?.strategy,
    }),

    searchSignalByStage: builder.query({
      query: () => `/apa/signals/stages`,
      method: "GET",
      forceRefetch: ({ currentArg, previousArg }) => currentArg !== previousArg,
    }),

    // avoid network call on empty/short ticker
    searchSignalByTicker: builder.query({
      async queryFn(arg, _api, _extra, fetchWithBQ) {
        const ticker = (arg?.ticker || "").trim();
        if (ticker.length < 2) return { data: [] };
        const res = await fetchWithBQ(
          `/apa/signals/tickers?limit=100&ticker=${encodeURIComponent(ticker)}`,
        );
        if (res.error) return { error: res.error };
        const data = Array.isArray(res.data?.rows) ? res.data.rows : res.data;
        return { data: Array.isArray(data) ? data : [] };
      },
      method: "GET",
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.ticker !== previousArg?.ticker,
    }),

    skipCheckExit: builder.mutation({
      query(body) {
        return {
          url: `/apa/signals/skip-check-k-exit`,
          method: "POST",
          body: JSON.stringify(body),
        };
      },
      invalidatesTags: ["ApaList"],
    }),
  }),
});

export const {
  useGetSignalsListQuery,
  useSyncSignalsListMutation,
  useSearchSignalByStageQuery,
  useSearchSignalByStrategyQuery,
  useSearchSignalByTickerQuery,
  useSkipCheckExitMutation,
} = apaApi;
